import moment from 'moment';
import _ from 'lodash';
import {Q} from '@nozbe/watermelondb';
import database from 'src/providers/watermelondb';

type ExportRateArgs = {
  sets: any[];
  events?: any[]; // HTTP or DB events; if empty we’ll use queriedEvents
  queriedEvents?: any[]; // DB-observed events (shape: e.set.id)
  program?: any; // for unit: 'per_min' | 'per_hour' | 'per_session'
  startDate?: string | number | Date;
  endDate?: string | number | Date;
  session?: any; // show session.sessionDate in the Date column when present
};

export const exportRate = async ({
  sets = [],
  events = [],
  queriedEvents = [],
  program,
  startDate,
  endDate,
  session,
}: ExportRateArgs) => {
  const rows: any[] = [
    [
      'Instance',
      'Date',
      'Time Stamp',
      'Duration Time',
      'Count',
      'Intensity',
      'Rate',
      'ABC',
    ],
  ];

  // ---------- helpers ----------
  const val = (v: any, alt: any) => (v !== undefined && v !== null ? v : alt);
  const setStart = (s: any) =>
    val(s?.startTimestamp, s?.start_timestamp ?? s?.start);
  const setEnd = (s: any) => val(s?.endTimestamp, s?.end_timestamp ?? s?.end);

  const normalizePromptsArray = (ps: any): any[] => {
    if (Array.isArray(ps)) {
      return ps;
    }
    if (ps == null) {
      return [];
    }
    if (typeof ps === 'string') {
      try {
        const parsed = JSON.parse(ps);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        if (ps.includes(',')) {
          return ps
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean);
        }
        return [ps]; // single ID string
      }
    }
    return [];
  };

  // ---------- filter & sort sets like the table ----------
  const inRangeSets =
    startDate && endDate
      ? sets.filter((s: any) => {
          const ts = Number(setStart(s)) || 0;
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return ts >= start.getTime() && ts <= end.getTime();
        })
      : sets;

  const sortKey = (s: any) =>
    s?.date ?? Number(setStart(s)) ?? Number(s?.session_date) ?? 0;
  const sortedSets = [...inRangeSets].sort((a, b) => sortKey(a) - sortKey(b));

  // ---------- choose events source & auto-detect shape ----------
  const allEvents = (events?.length ? events : queriedEvents || []) as any[];

  const getSetIdFromEvent = (e: any) =>
    e?.set?.id ?? e?.set_id ?? e?.setId ?? null;

  const eventsBySetId: Record<string, any[]> = _.groupBy(allEvents, (e: any) =>
    String(getSetIdFromEvent(e) ?? ''),
  );

  // ---------- prefetch prompts from SETS (ABC shown under rate in the table) ----------
  const collectPromptIds = (): string[] => {
    const ids = new globalThis.Set<string>();
    for (const s of sortedSets) {
      for (const p of normalizePromptsArray(s?.prompts)) {
        if (typeof p === 'string') {
          ids.add(p);
        } else if (p?.id) {
          ids.add(p.id);
        }
      }
    }
    return Array.from(ids);
  };

  const promptNameById: Record<string, string> = {};
  const promptIds = collectPromptIds();

  const fetchByIdBatches = async (
    table: string,
    ids: string[],
    idField: string = 'id',
  ) => {
    const out: any[] = [];
    for (const ch of _.chunk(ids, 500)) {
      const _rows = await database
        .get(table)
        .query(Q.where(idField, Q.oneOf(ch)))
        .fetch();
      out.push(..._rows);
    }
    return out;
  };

  if (promptIds.length > 0) {
    // prompts first
    const pr = await fetchByIdBatches('prompts', promptIds, 'id');
    for (const row of pr) {
      promptNameById[row.id] = row?.name || row?.acronym || '';
    }
    // fallback: program_prompt
    const missing = promptIds.filter(id => !promptNameById[id]);
    if (missing.length > 0) {
      const ppr = await fetchByIdBatches('program_prompt', missing, 'id');
      for (const pp of ppr) {
        const n = pp?.name || pp?.acronym || '';
        if (n) {
          promptNameById[pp.id] = n;
        }
      }
      // hop via prompt_id -> prompts
      const stillMissing = missing.filter(id => !promptNameById[id]);
      if (stillMissing.length > 0 && ppr.length > 0) {
        const baseIds = _.uniq(
          ppr.map((x: any) => x?.prompt_id || x?.promptId).filter(Boolean),
        );
        if (baseIds.length > 0) {
          const basePrompts = await fetchByIdBatches('prompts', baseIds, 'id');
          const baseMap: Record<string, string> = {};
          for (const b of basePrompts) {
            baseMap[b.id] = b?.name || b?.acronym || '';
          }
          for (const pp of ppr) {
            const nm = baseMap[pp?.prompt_id || pp?.promptId];
            if (nm && !promptNameById[pp.id]) {
              promptNameById[pp.id] = nm;
            }
          }
        }
      }
    }
  }

  const namesForSetPrompts = (s: any): string[] => {
    const out: string[] = [];
    for (const p of normalizePromptsArray(s?.prompts)) {
      if (typeof p === 'string') {
        const n = promptNameById[p];
        if (n) {
          out.push(n);
        }
      } else if (p?.name) {
        out.push(p.name);
      } else if (p?.acronym) {
        out.push(p.acronym);
      } else if (p?.id && promptNameById[p.id]) {
        out.push(promptNameById[p.id]);
      }
    }
    return out;
  };

  // ---------- build CSV rows ----------
  for (let i = 0; i < sortedSets.length; i++) {
    const set = sortedSets[i];
    const setId = String(set?.id ?? '');
    const setEvents = eventsBySetId[setId] || [];

    // Date column matches the UI pattern used elsewhere
    const dateStr = session
      ? moment(session?.sessionDate).format('ll')
      : moment(set?.session_date).format('ll');

    const startTs = setStart(set);
    const endTs = setEnd(set);

    const timeStamp =
      `${startTs ? moment(startTs).format('hh:mm:ss A') : ''} - ` +
      `${endTs ? moment(endTs).format('hh:mm:ss A') : 'Incomplete'}`;

    // Duration Time (hr/min/sec like the UI)
    let durationStr = 'Incomplete';
    let durationMin = 0; // for rate denominator
    if (startTs && endTs) {
      const dur = moment.duration(moment(endTs).diff(moment(startTs)));
      const h = dur.hours();
      const m = dur.minutes();
      const s = dur.seconds();
      durationStr =
        (h > 0 ? `${h} hr ` : '') +
        (m > 0 ? `${m} min ` : '') +
        (s >= 0 ? `${s} sec` : '').trim();
      durationMin = dur.asMinutes();
    }

    const count = setEvents.length;

    // Intensity from the set (matches table)
    const intensity = _.capitalize(set?.intensity || '');

    // Rate — follow table logic (unit-aware); if no end timestamp => TBD
    let rateStr: string = '';
    if (!endTs) {
      rateStr = 'To Be Determined';
    } else {
      // mimic table’s guard: avoid divide-by-zero by using 0.01 in tiny windows
      const safeMinutes = durationMin || 0.01;
      let denominator = safeMinutes; // per_min default
      switch (program?.unit) {
        case 'per_min':
          denominator = safeMinutes;
          break;
        case 'per_hour':
          denominator = safeMinutes / 60 || 0.01;
          break;
        case 'per_session':
          denominator = 1;
          break;
        default:
          denominator = safeMinutes; // sensible default
      }
      rateStr = (count / denominator).toFixed(2);
    }

    // ABC — from set.prompts
    const abc = namesForSetPrompts(set)
      .map(n => (typeof n === 'string' ? n.replaceAll('"', "'") : ''))
      .filter(Boolean)
      .join('\n');

    rows.push([
      `${i + 1}`, // Instance
      dateStr, // Date
      timeStamp, // Time Stamp
      durationStr, // Duration Time
      `${count}`, // Count
      intensity, // Intensity
      rateStr, // Rate
      abc, // ABC
    ]);
  }

  return rows;
};
